import { useTranslation } from 'react-i18next';
import Button from '../base/Button';

const Hero = () => {
  const { t } = useTranslation('common');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/50"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-500/15 to-pink-500/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 backdrop-blur-sm mb-8">
              <span className="text-cyan-400 text-sm font-medium">
                {t('hero.badge')}
              </span>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6">
              <span className="text-white block">
                {t('hero.title.line1')}
              </span>
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent block">
                {t('hero.title.line2')}
              </span>
              <span className="text-white block">
                {t('hero.title.line3')}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed mb-8 sm:mb-10 max-w-2xl mx-auto lg:mx-0">
              {t('hero.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => scrollToSection('integration')}
                icon={<i className="ri-rocket-line"></i>}
              >
                {t('hero.cta.primary')}
              </Button>
              <a
                href="/docs/en/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center font-medium transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-white bg-transparent px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg rounded-xl"
              >
                <i className="ri-book-open-line mr-2"></i>
                {t('hero.cta.secondary')}
              </a>
              <a
                href="/demo.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center font-medium transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg rounded-xl"
              >
                <i className="ri-play-circle-line mr-2"></i>
                {t('hero.cta.demo')}
              </a>
            </div>
          </div>

          {/* Right Visual */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Main Illustration */}
              <img 
                src="/images/hero-illustration.png"
                alt="Crypto Payment Network"
                className="w-full max-w-lg h-auto object-contain"
              />
              
              {/* Floating Icons */}
              <div className="hidden sm:flex absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center animate-bounce">
                <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
              </div>
              <div className="hidden md:flex absolute top-1/3 -right-6 w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center animate-pulse delay-500">
                <i className="ri-secure-payment-line text-xl text-white"></i>
              </div>
              <div className="hidden lg:flex absolute bottom-1/4 -left-6 w-12 h-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-ping">
                <i className="ri-shield-check-line text-lg text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <button 
          onClick={() => scrollToSection('stats')}
          className="w-8 h-12 border-2 border-cyan-400 rounded-full flex items-end justify-center p-2"
        >
          <div className="w-1 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
        </button>
      </div>
    </section>
  );
};

export default Hero;